import { useContext } from 'react';
import { T } from 'gt-react';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import { AuthContext } from 'src/context/auth.context';
import { useSops } from 'src/api/rpc/hooks/agent/useOperatorSops';
import {
  siteOf,
  sopLabel,
} from 'src/pages/authenticated/operator/procedureDisplay';
import { ProcedureOptionLabel } from 'src/pages/authenticated/operator/ProcedureOptionLabel';

/** Reads a `string[]` of SOP ids off a step config, tolerating an absent or malformed value. */
export function parseProcedureIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string');
}

/**
 * Checkbox list of the org's procedures, bound to a `string[]` of SOP ids on a step config.
 * The compact counterpart to the agent Procedures tab's full-page DataTable — same allow-list
 * semantics, sized for a node config panel.
 */
export function ProcedureMultiSelect({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (sopIds: string[]) => void;
  disabled?: boolean;
}) {
  const { currentUser } = useContext(AuthContext);
  const { sops, isLoading } = useSops({
    organizationId: currentUser?.organizationId,
  });
  const selected = new Set(value);

  if (isLoading) {
    return (
      <p className="text-[11px] text-muted-foreground">
        <T>Loading procedures...</T>
      </p>
    );
  }

  if (sops.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        <T>
          No procedures yet. Teach one from Agents → Procedures, then attach it
          here.
        </T>
      </p>
    );
  }

  return (
    <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
      {sops.map((sop) => {
        const site = siteOf(sop);
        return (
          <div key={sop.id} className="flex items-start gap-2">
            <Checkbox
              id={`procedure-${sop.id}`}
              checked={selected.has(sop.id)}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onChange(
                  checked === true
                    ? [...value, sop.id]
                    : value.filter((id) => id !== sop.id),
                )
              }
              aria-label={sopLabel(sop)}
            />
            <Label
              htmlFor={`procedure-${sop.id}`}
              className="min-w-0 cursor-pointer text-xs font-normal leading-tight"
            >
              <ProcedureOptionLabel sop={sop} />
              {site ? (
                <span className="block truncate text-[11px] text-muted-foreground">
                  {site}
                </span>
              ) : null}
            </Label>
          </div>
        );
      })}
    </div>
  );
}
